import { Badge, Stack, Text } from '@repletoryreach/sdk-ui';
import { Switch } from '@repletoryreach/sdk-ui/inputs';
import type { admin } from '@repletoryreach/sdk-api';
import { TwoLineCell } from '../../../../components/TwoLineCell';
import { formatInteger } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { ENGLISH, linesInCode, missingLines, qualityColour } from '../languages';

const MUTED = 'var(--rr-color-text-muted)';

/** Its own name, as customers see it on the menu, over the English name and the code. */
export function LanguageNameCell({ language }: { language: admin.AdminLanguage }) {
  return (
    <TwoLineCell
      primary={language.native_name}
      secondary={<>{language.name} · <code>{language.code}</code></>}
      title={language.native_name}
    />
  );
}

/** How well the AI writes it. */
export function QualityBadge({ quality }: { quality: string }) {
  const t = useT();
  return (
    <Badge size="xs" variant="light" color={qualityColour(quality)}>
      {t(`billing.languages.quality.${quality}`, quality)}
    </Badge>
  );
}

/** Meta's template codes, or a plain word that templates cannot be in it. */
export function MetaCodesCell({ language }: { language: admin.AdminLanguage }) {
  const t = useT();
  if (language.meta_codes.length === 0) return <Text role="bodySmall" c={MUTED}>{t('billing.languages.meta.none')}</Text>;
  return <Text role="bodySmall" style={{ overflowWrap: 'anywhere' }}><code>{language.meta_codes.join(', ')}</code></Text>;
}

/** Whether voice notes in it can be turned into words, with the transcription code. */
export function SpeechCell({ language }: { language: admin.AdminLanguage }) {
  const t = useT();
  return language.speech_code
    ? <TwoLineCell primary={t('common.yes')} secondary={<code>{language.speech_code}</code>} />
    : <Text role="bodySmall" c={MUTED}>{t('common.no')}</Text>;
}

/** "30 of 36" built-in lines, with what is edited, read by a native speaker and changed in English since. */
export function LinesCell({ language }: { language: admin.AdminLanguage }) {
  const t = useT();
  const w = language.wordings;
  if (linesInCode(language.code)) {
    return <TwoLineCell primary={t('billing.languages.lines.all', { total: formatInteger(w.total) })} secondary={t('billing.languages.lines.inCode')} />;
  }
  const missing = missingLines(language);
  return (
    <Stack gap={2}>
      <Text role="bodySmall" fw={600}>
        {t('billing.languages.lines.count', { translated: formatInteger(w.translated), total: formatInteger(w.total) })}
      </Text>
      <Text role="caption" c={MUTED}>
        {t('billing.languages.lines.detail', { edited: formatInteger(w.edited), reviewed: formatInteger(w.reviewed) })}
      </Text>
      {w.stale > 0 && (
        <Text role="caption" c="var(--rr-color-warning)">{t('billing.languages.lines.stale', { count: formatInteger(w.stale) })}</Text>
      )}
      {missing > 0 && language.enabled && (
        <Text role="caption" c="var(--rr-color-warning)">{t('billing.languages.lines.missing', { count: formatInteger(missing) })}</Text>
      )}
    </Stack>
  );
}

/** On or off. The page confirms before it changes anything; English stays on. */
export function LanguageSwitch({ language, busy, onToggle }: {
  language: admin.AdminLanguage;
  busy: boolean;
  onToggle: (l: admin.AdminLanguage, on: boolean) => void;
}) {
  const t = useT();
  const locked = language.code === ENGLISH;
  return (
    <span title={locked ? t('billing.languages.englishStays') : undefined}>
      <Switch
        size="sm"
        checked={language.enabled}
        disabled={busy || locked}
        onChange={(on) => onToggle(language, on)}
        label={language.enabled ? t('billing.languages.state.on') : t('billing.languages.state.off')}
        aria-label={t('billing.languages.switchLabel', { name: language.name })}
      />
    </span>
  );
}
